/**
 * The z-index ladder: every layer of the screen, bottom to top, in one file.
 *
 * @why    Until now the ladder only existed as numbers scattered across eleven components,
 *         and the only way to learn that the help flow sits above the coverage banner was to
 *         find the comment in GuidedFlowModal. PrimeVue then brings a ladder of its own
 *         (`modal: 1100, overlay: 1000`) and manages it at runtime, so a library Dialog left
 *         on the defaults would open UNDER the manual-order modal it is meant to replace.
 *         Both ladders have to be written against the same list, and this is that list.
 *
 * @rule   These are the numbers the screens had before the migration, 1:1. Stage 1 wrote them
 *         down and pointed PrimeVue at them; stage 2 replaced every literal in the components
 *         with its name — `z-index: var(--bz-z-modal)`, from the copy of this ladder in
 *         src/ui/tokens.css, because CSS cannot import JavaScript — with the visual harness
 *         proving nothing moved. Changing a number is a visible change and belongs to the stage
 *         that migrates that layer; it changes here AND in tokens.css (a test holds them equal).
 *
 * Only layers that compete in the document's root stacking context are listed. The camera
 * preview is its own stacking context (`.camera-preview`, z-index 0 — measured in the browser),
 * so the flash (999) and the shutter (1001) inside it never compete with anything here.
 * CameraScreen's own overlays are the opposite case: `.retake-aim`, `.last-photo-stack` and
 * `.duplicate-hint` are absolute children of `.camera-screen`, which forms no stacking context,
 * so they do compete here and are on the ladder.
 *
 * Intercom and the PrimeUI licence banner are deliberately NOT on this ladder: both put
 * themselves near the maximum integer, above any number of ours. Intercom is governed by
 * geometry (the v4.46.2 fix) and the licence banner by having a valid licence.
 */
export const Z = Object.freeze({
    /** CameraScreen, immersive mode: `.camera-preview`, full-bleed; everything inside it is local. */
    cameraPreview: 0,
    /**
     * CameraScreen, immersive mode: the order list (`.main-container`, `.classic-layout`) and the
     * selected order's bar (`.order-info-wrapper`), both over the preview.
     */
    orderList: 5,
    /**
     * The selected order's code, over its bar: `.order-id` (CameraScreen, immersive mode) and
     * `.rbe-order-bar` (the RBE pilot's bar, on a phone). Both are children of `.camera-screen`,
     * so they sit in the root context too — the stage-1 ladder had missed them.
     */
    orderCode: 6,
    /** CameraScreen, immersive mode: the `.header` over the preview. */
    header: 10,
    /** CameraScreen `.retake-aim` — what to aim at, during a retake. */
    retakeAim: 900,
    /** CameraScreen `.last-photo-stack` */
    photoStack: 999,
    /** CameraScreen `.duplicate-hint` — and the two critical-lane popups below share it. */
    duplicateHint: 1000,
    /** AnalysisPopupComponent `.analysis-popup-overlay`, TimeoutPopupComponent `.timeout-popup-overlay`. */
    lanePopup: 1000,
    /** ManualOrderModal `.manual-order-overlay`; CameraScreen `.immersive-modal-overlay`. */
    modal: 2000,
    /** CameraScreen `.rider-list-overlay` */
    riderList: 9000,
    /** App.vue `.coverage-thanks-toast` */
    coverageThanks: 9550,
    /** PhotoCoverageAlertComponent — the banner. Below the help flow: asking for help outranks being nudged. */
    coverageBanner: 9600,
    /** GuidedFlowModal — the help flow. */
    helpFlow: 9700,
    /** ChangelogComponent `.changelog-overlay`, NotificationComponent `.notification-overlay`. */
    blocking: 9999,
    /**
     * App.vue `.rider-toast-container` — the top of the ladder. A rider at the counter is the
     * kitchen's real-time reality and no form, dialog or banner may hide it.
     */
    riderToasts: 10000,
});

/**
 * Where PrimeVue's own ladder starts, per component category.
 *
 * PrimeVue does not paint an overlay at these numbers: it paints it ABOVE them and stacks,
 * using `@primeuix/utils/zindex` (read in 5.0.1). An overlay opened on its own lands at
 * `base + 1`. One opened over an overlay of the same category lands at `previous + 1`. One
 * opened over an overlay of a DIFFERENT category lands at `previous + base + 1` — so a
 * dropdown inside a dialog climbs by the whole `overlay` base.
 *
 * That last rule is why the dropdown bases are small rather than PrimeVue's 1000:
 *   - on its own, a dropdown only has to clear the screen chrome, so `Z.header` (+1) does it;
 *   - inside a dialog, a base of 10 keeps it in that dialog's band. With the default 1000, a
 *     dropdown inside the help flow (9701) would land at 10702 — above the rider toasts.
 * It sits at the edge on purpose, and that has a cost: a new element in the root stacking
 * context between the header and 900 would cover every open dropdown, and the inventory test
 * only watches 900 and up. Such an element goes on this ladder, and the dropdown base moves
 * above it — anything up to 297 still keeps the help flow's dropdowns under the toasts.
 *
 * `modal` is the deliberate-modal band, where the manual-order modal already lives, so the
 * first Dialog (stage 5) opens exactly where the modal it replaces does.
 *
 * The four deliberate dialogs (stage 5) do NOT use this arithmetic: `BzDialog` sets
 * `autoZIndex: false` and paints at exactly its band of `Z`. On it, `blocking` (9999) + 1 tied with
 * the rider toasts, and a dialog opened over another landed at that one + 1 whatever its base (the
 * blocking notification over the manual-order modal, at 2001, under the coverage banner). The
 * cost: a library dropdown opened INSIDE a BzDialog would not see it on this ladder and open under
 * it — none does today; the stage that puts one there has to solve that first.
 */
export const PRIMEVUE_Z_INDEX = Object.freeze({
    modal: Z.modal,
    overlay: Z.header,
    menu: Z.header,
    tooltip: Z.header,
});
